import React, {useCallback, useRef} from 'react';
import { ErrorDetails, formatMessageBundle } from '@unifocus/js-utils';
import { Button, Collapse, CollapsePanel, Paragraph, Toolbar } from '@unifocus/react-components';
import messages from '../localized-messages';

export interface ErrorReportingCollapsiblePanelProperties {
   errorDetails: ErrorDetails;
}

export const ErrorReportingCollapsiblePanel = (props: ErrorReportingCollapsiblePanelProperties) => {
   const title = formatMessageBundle(messages, 'res_viewErrorReport');
   const ref = useRef<HTMLDivElement>(null);
   const copyClicked = useCallback(() => {
      if (ref.current) {
         const range = document.createRange();
         const selection = window.getSelection();
         range.selectNode(ref.current);
         selection?.removeAllRanges(); // clear current selection
         selection?.addRange(range); // to select text
         document.execCommand("copy");
         selection?.removeAllRanges();// to deselect
      }
   },[]);

   return (
      <Collapse>
         <CollapsePanel
            panelId="errorDetails"
            header={title}
         >
            <div ref={ref}>
               {errorDetailsElements(props.errorDetails)}
            </div>

            <Toolbar
               rightContent={<Button onClick={copyClicked}>Copy</Button>}
            />
         </CollapsePanel>
      </Collapse>
   );
};

export const errorDetailsElements = (errorDetails: ErrorDetails) => {
   const applicationId = formatMessageBundle(messages, 'res_applicationId');
   const build = formatMessageBundle(messages, 'res_build');
   const at = formatMessageBundle(messages, 'res_at');
   const errorCode = formatMessageBundle(messages, 'res_errorCode');
   const errorMessage = formatMessageBundle(messages, 'res_errorMessage');
   const partnerCode = formatMessageBundle(messages, 'res_partnerCode');
   const databaseId = formatMessageBundle(messages, 'res_databaseId');
   const propertyId = formatMessageBundle(messages, 'res_propertyId');
   const correlationId = formatMessageBundle(messages, 'res_correlationId');
   const urlAllCaps = formatMessageBundle(messages, 'res_urlAllCaps');
   const method = formatMessageBundle(messages, 'res_method');
   const userAgent = formatMessageBundle(messages, 'res_userAgent');
   const stackTrace = errorDetails.stackTrace;
   const unknown = formatMessageBundle(messages, 'res_unknown');

   return (
      <Paragraph>
         {<div>{`${applicationId}: ${errorDetails.applicationId || unknown}`}</div>}
         {<div>{`${build}: ${errorDetails.buildNo || unknown}`}</div>}
         {<div>{`${at}: ${errorDetails.timeStamp || unknown}`}</div>}
         {<div>{`${errorCode}: ${errorDetails.errorCode || unknown}`}</div>}
         {<div>{`${errorMessage}: ${errorDetails.message || unknown}`}</div>}
         {<div>{`${partnerCode}: ${errorDetails.partnerCode || unknown}`}</div>}
         {<div>{`${databaseId}: ${errorDetails.databaseId || unknown}`}</div>}
         {<div>{`${propertyId}: ${errorDetails.propertyId || unknown}`}</div>}
         {<div>{`${correlationId}: ${errorDetails.correlationId || unknown}`}</div>}
         {<div>{`${urlAllCaps}: ${errorDetails.requestUrl || unknown}`}</div>}
         {<div>{`${method}: ${errorDetails.requestMethod || unknown}`}</div>}
         {<div>{`${userAgent}: ${errorDetails.userAgent || unknown}`}</div>}
         {stackTraceElement(stackTrace)}
      </Paragraph>
   );
};

const stackTraceElement = (stackTrace?: string) => {
   if (stackTrace) {
      return (
         <>
            <div>---------------------------------</div>
            {("" + stackTrace).split('\n').map(((value, index) => {
               return (<div key={index}>{value}</div>);
            }))}
         </>
      );
   }

   return undefined;
};
